import { useQuery } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { PlayerDto } from '@/types/dtos/player/response/player.dto';
import type { GetPlayersDto } from '@/types/dtos/player/request/get-players.dto';

export const GET_PLAYERS_QUERY_KEY = 'get-players';

export const useGetPlayersQuery = (data?: GetPlayersDto, options?: { enabled?: boolean }) => {
  const { getToken } = useAuth();
  const enabled = !!data && (options?.enabled ?? true);

  return useQuery({
    // Include entityId and entityType in query key so each entity has its own cache entry
    // This prevents one component from triggering queries for other entities
    queryKey: [GET_PLAYERS_QUERY_KEY, data?.entityId, data?.entityType, data?.eventSlotId],
    queryFn: async () => {
      const token = await getToken();
      return getPlayers({ token: token ?? '', data: data! });
    },
    enabled,
    // Current + Next both mount this query; without staleTime the second mount refetches (~59KB).
    staleTime: 5_000,
    placeholderData: (previousData) => previousData,
  });
};

const getPlayers = async ({ token, data }: { token: string; data: GetPlayersDto }): Promise<PlayerDto[]> => {
  const params = new URLSearchParams({
    entityId: data.entityId,
    entityType: data.entityType,
  });
  if (data.eventSlotId) {
    params.set('eventSlotId', data.eventSlotId);
  }
  const url = `${import.meta.env.VITE_API_URL}/player?${params.toString()}`;
  return sendJSONRequest({ url, method: METHOD.GET, token });
};
